<template lang="pug">
.typing-overlay(v-if="active")
  .typing-overlay__preview(v-if="buffer") {{ buffer }}
  .typing-overlay__saved(v-if="lastSubmitted") {{ lastSubmitted }}

  .typing-overlay__search-trigger(
    v-if="showMobileInput && isMobile && !showSearchInput"
    @click="openSearchInput"
  )
    span.material-symbols-outlined keyboard

  .typing-overlay__search-bar(v-if="showMobileInput && showSearchInput")
    .input-container.typing-overlay__search-field
      input.input(
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        autocomplete="ño"
        @keyup.enter="submitSearchInput"
      )
      label.label(:class="{ show: searchQuery }") {{ inputLabel }}
    span.material-symbols-outlined.typing-overlay__search-close(@click="closeSearchInput") close
</template>

<script>
import { normalizeScanCode } from '@/helpers/scanCode'

export default {
  name: 'KeyboardTypingOverlay',

  props: {
    active: {
      type: Boolean,
      default: false
    },
    inputLabel: {
      type: String,
      default: ''
    },
    showMobileInput: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      buffer: '',
      lastSubmitted: '',
      showSearchInput: false,
      searchQuery: '',
      isMobile: false
    }
  },

  watch: {
    active (value) {
      if (!value) this.reset()
    }
  },

  mounted () {
    this.checkMobile()
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.checkMobile)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.checkMobile)
  },

  methods: {
    checkMobile () {
      this.isMobile = window.innerWidth <= 768
    },

    reset () {
      this.buffer = ''
      this.lastSubmitted = ''
      this.showSearchInput = false
      this.searchQuery = ''
    },

    openSearchInput () {
      this.showSearchInput = true

      this.$nextTick(() => {
        const input = this.$refs.searchInput
        if (input) input.focus()
      })
    },

    closeSearchInput () {
      this.showSearchInput = false
      this.searchQuery = ''
    },

    submitValue (value) {
      const trimmed = normalizeScanCode(value)
      if (!trimmed) return

      this.lastSubmitted = trimmed
      this.$emit('submit', trimmed)
    },

    submit () {
      this.submitValue(this.buffer)
      this.buffer = ''
    },

    submitSearchInput () {
      const query = this.searchQuery.trim()
      if (!query) return

      this.submitValue(query)
      this.searchQuery = ''

      this.$nextTick(() => {
        const input = this.$refs.searchInput
        if (input) input.focus()
      })
    },

    isTypingInField (target) {
      if (!target) return false
      const tag = target.tagName
      return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA'
    },

    onKeydown (event) {
      if (!this.active) return
      if (this.isTypingInField(event.target)) return

      if (event.key === 'Enter') {
        event.preventDefault()
        this.submit()
        return
      }

      if (event.key === 'Tab') {
        if (!this.buffer) return
        event.preventDefault()
        this.submit()
        return
      }

      if (event.key === 'Backspace') {
        this.buffer = this.buffer.slice(0, -1)
        return
      }

      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        this.buffer += event.key
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.typing-overlay
  position fixed
  inset 0
  pointer-events none
  z-index 100

.typing-overlay__preview
  position fixed
  left 50%
  top 55%
  transform translateX(-50%)
  font-size 4rem
  font-weight 700
  letter-spacing .05em
  text-transform uppercase
  color rgba(0, 0, 0, .75)
  background-color #fff
  padding .15em .35em
  user-select none

.typing-overlay__saved
  position fixed
  right 1.5rem
  bottom 1.5rem
  font-size 1.6rem
  font-weight 600
  letter-spacing .04em
  text-transform uppercase
  color rgba(0, 0, 0, .55)
  background-color #fff
  padding .25em .5em
  user-select none

.typing-overlay__search-trigger
  position fixed
  left 1rem
  bottom 1rem
  z-index 110
  display flex
  align-items center
  justify-content center
  width 2.75rem
  height 2.75rem
  background-color #fff
  border-radius 50%
  box-shadow 0 2px 8px rgba(0, 0, 0, .15)
  cursor pointer
  user-select none
  pointer-events auto

  .material-symbols-outlined
    font-size 1.5rem
    opacity .7

  &:hover .material-symbols-outlined
    opacity 1

.typing-overlay__search-bar
  position fixed
  left 0
  right 0
  bottom 0
  z-index 111
  display flex
  align-items flex-end
  gap .75rem
  padding .75rem 1rem
  background-color var(--background-principal, #fff)
  border-top 1px solid rgba(0, 0, 0, .1)
  box-shadow 0 -2px 12px rgba(0, 0, 0, .08)
  pointer-events auto

.typing-overlay__search-field
  flex 1
  margin 0
  min-width 0

.typing-overlay__search-close
  font-size 2.25rem
  cursor pointer
  opacity .55
  flex-shrink 0
  user-select none
  line-height 1
  padding-bottom .15em

  &:hover
    opacity 1
</style>



// WEBPACK FOOTER //
// src/components/keyboard/TypingOverlay.vue